import { useState } from 'react';
import { Link, Navigate, useNavigate } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { INDUSTRY_KEYS, INDUSTRY_OPTIONS, INDUSTRY_PRESETS, workspaceSlugSchema } from '@stoplord/shared';
import { authApi } from '@/services/auth';
import { useAuth } from '@/store/auth';
import { errorMessage } from '@/lib/api';
import { Button, Field, Input, Select } from '@/components/ui';
import { AuthLayout } from './AuthLayout';
import { handleFormError } from '../shared/forms';

const schema = z
  .object({
    companyName: z.string().trim().min(2, 'Enter your company name').max(120),
    workspace: workspaceSlugSchema,
    industry: z.enum(INDUSTRY_KEYS),
    ownerName: z.string().trim().min(2, 'Enter your name'),
    email: z.string().trim().email('Enter a valid email'),
    password: z.string().min(10, 'At least 10 characters').regex(/[a-zA-Z]/, 'Include a letter').regex(/\d/, 'Include a number'),
    confirm: z.string(),
    branchName: z.string().trim().max(100).optional(),
  })
  .refine((v) => v.password === v.confirm, { path: ['confirm'], message: 'Passwords do not match' });
type Form = z.infer<typeof schema>;

const slugify = (s: string) => s.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '').slice(0, 40);

export function RegisterPage() {
  const status = useAuth((s) => s.status);
  const navigate = useNavigate();
  const [error, setError] = useState<string | null>(null);
  const { register, handleSubmit, formState, watch, setValue, setError: setFieldError } = useForm<Form>({
    resolver: zodResolver(schema),
    defaultValues: { industry: 'it_software', workspace: '', companyName: '' },
  });
  const industry = watch('industry');
  if (status === 'authenticated') return <Navigate to="/" replace />;
  const preset = INDUSTRY_PRESETS[industry];

  const onSubmit = handleSubmit(async ({ confirm: _c, ...v }) => {
    setError(null);
    try {
      await authApi.register({ ...v, branchName: v.branchName || undefined, timezone: Intl.DateTimeFormat().resolvedOptions().timeZone, currency: 'INR' });
      navigate('/', { replace: true });
    } catch (e) {
      handleFormError(e, setFieldError);
      setError(errorMessage(e));
    }
  });

  return (
    <AuthLayout wide title="Create your company workspace" subtitle="Your data is kept completely separate from every other company on StopLord.">
      <form onSubmit={onSubmit} className="space-y-4" noValidate>
        <div className="grid gap-4 sm:grid-cols-2">
          <Field label="Company name" required error={formState.errors.companyName?.message}>
            {(p) => <Input {...p} {...register('companyName', { onChange: (e) => !formState.dirtyFields.workspace && setValue('workspace', slugify(e.target.value)) })} />}
          </Field>
          <Field label="Workspace" required error={formState.errors.workspace?.message} hint="Used to sign in. Lowercase letters, numbers and hyphens.">
            {(p) => <Input {...p} autoCapitalize="none" {...register('workspace')} />}
          </Field>
        </div>
        <Field label="Industry" required hint={`Sets up ${preset.departments.length} departments, ${preset.leadStages.length} pipeline stages and terminology (“${preset.terminology.client.plural}”). You can change everything later.`}>
          {(p) => (
            <Select {...p} {...register('industry')}>
              {INDUSTRY_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
            </Select>
          )}
        </Field>
        {(industry === 'healthcare_admin' || industry === 'clinic_diagnostics') && (
          <p className="rounded-lg bg-info-soft px-3 py-2 text-xs text-info">The healthcare preset covers administrative workflows (enquiries, staff, appointments). StopLord is not a clinical medical-record system.</p>
        )}
        <div className="grid gap-4 sm:grid-cols-2">
          <Field label="Your name" required error={formState.errors.ownerName?.message}>{(p) => <Input {...p} autoComplete="name" {...register('ownerName')} />}</Field>
          <Field label="First branch" hint="Defaults to “Head Office”.">{(p) => <Input {...p} {...register('branchName')} />}</Field>
        </div>
        <Field label="Work email" required error={formState.errors.email?.message}>{(p) => <Input {...p} type="email" autoComplete="email" {...register('email')} />}</Field>
        <div className="grid gap-4 sm:grid-cols-2">
          <Field label="Password" required error={formState.errors.password?.message}>{(p) => <Input {...p} type="password" autoComplete="new-password" {...register('password')} />}</Field>
          <Field label="Confirm password" required error={formState.errors.confirm?.message}>{(p) => <Input {...p} type="password" autoComplete="new-password" {...register('confirm')} />}</Field>
        </div>
        {error && <p className="rounded-lg bg-danger-soft px-3 py-2 text-sm text-danger" role="alert">{error}</p>}
        <Button type="submit" className="w-full justify-center" loading={formState.isSubmitting}>Create workspace</Button>
      </form>
      <p className="mt-6 text-center text-sm text-ink-muted">Already have a workspace? <Link to="/login" className="font-medium text-brand-ink hover:underline">Sign in</Link></p>
    </AuthLayout>
  );
}
